import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CheckCircle2, ShieldCheck, Copy, ArrowRight } from "lucide-react";
import { CompassLogo } from "../components/CompassLogo";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { api, API, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";

/**
 * Forced first-login wizard. Three steps:
 *   1) `password` — user changes the temporary password issued by the admin.
 *   2) `mfa-setup` — user scans the QR with Google Authenticator and types
 *      the first 6-digit code.
 *   3) `backup-codes` — display 10 single-use backup codes (download them).
 * Lands on `/first-login?token=<step_token>&u=<username>` from the login page
 * after the backend returns `password_change_required: true`.
 */
export default function FirstLoginPage() {
  const { t, i18n } = useTranslation();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();

  const initialToken = params.get("token") || "";
  const presetUsername = params.get("u") || "";

  const [step, setStep] = useState("password"); // password | mfa-setup | backup-codes
  const [userRole, setUserRole] = useState("");
  const [stepToken, setStepToken] = useState(initialToken);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [mfaCode, setMfaCode] = useState("");
  const [backupCodes, setBackupCodes] = useState([]);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [otpauthUri, setOtpauthUri] = useState("");
  const [secret, setSecret] = useState("");
  const codeRef = useRef(null);

  useEffect(() => {
    if (!initialToken) {
      navigate("/login", { replace: true });
    }
  }, [initialToken, navigate]);

  useEffect(() => {
    if (step === "mfa-setup" && codeRef.current) {
      codeRef.current.focus();
    }
  }, [step]);

  const submitPassword = async (e) => {
    e.preventDefault();
    setError("");
    if (newPassword.length < 8) {
      setError(t("firstLogin.password_must_be_at_least"));
      return;
    }
    if (!/[A-Z]/.test(newPassword)) {
      setError(t("firstLogin.password_must_contain_at_least"));
      return;
    }
    if (!/[0-9]/.test(newPassword)) {
      setError(t("firstLogin.password_must_contain_at_least_2"));
      return;
    }
    if (!/[!@#$%^&*()_+\-=[\]{};:,.?/]/.test(newPassword)) {
      setError(t("firstLogin.password_must_contain_at_least_3"));
      return;
    }
    if (newPassword !== confirmPassword) {
      setError(t("firstLogin.passwords_do_not_match"));
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/change-password", {
        mfa_token: stepToken,
        new_password: newPassword,
      });
      // Cookie is set; we no longer need the step token. Move on to MFA setup.
      if (data?.access_token) localStorage.setItem("sc_token", data.access_token);
      setUserRole(data?.role || "");
      // Fire MFA setup right away.
      const setup = await api.post("/auth/mfa/setup");
      setOtpauthUri(setup.data.otpauth_uri);
      setSecret(setup.data.secret);
      setStep("mfa-setup");
      setStepToken("");
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const submitMfa = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/mfa/confirm", { code: mfaCode });
      setBackupCodes(data?.backup_codes || []);
      setStep("backup-codes");
    } catch (e) {
      setError(formatApiError(e));
      setMfaCode("");
      if (codeRef.current) codeRef.current.focus();
    } finally {
      setSubmitting(false);
    }
  };

  const finish = async () => {
    await refreshUser();
    navigate("/", { replace: true });
  };

  const copyBackupCodes = () => {
    const text = backupCodes.join("\n");
    if (navigator.clipboard) navigator.clipboard.writeText(text);
  };

  const downloadBackupCodes = () => {
    const blob = new Blob([
      `Square & Compass — ${t("firstLogin.backup_codes")}\n` +
      `${t("firstLogin.account")}: ${presetUsername}\n` +
      `${t("firstLogin.generated")}: ${new Date().toISOString()}\n\n` +
      backupCodes.join("\n") + "\n",
    ], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `square-compass-backup-codes-${presetUsername}.txt`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 0);
  };

  const qrUrl = otpauthUri ? `${API}/auth/mfa/qr` : "";

  return (
    <div className="auth-bg flex items-center justify-center px-4">
      <div className="absolute top-4 right-4 z-20">
        <LanguageSwitcher />
      </div>
      <div className="gold-card w-full max-w-md relative z-10 px-10 py-12" data-testid="first-login-card">
        <div className="flex flex-col items-center text-center">
          <CompassLogo size={84} variant="dark" />
          <h1
            style={{
              fontFamily: "Cinzel, serif",
              fontSize: "1.05rem",
              letterSpacing: "0.18em",
              color: "#0d1b2a",
              marginTop: "1rem",
            }}
            data-testid="first-login-title"
          >
            {step === "password" && (t("firstLogin.set_a_new_password"))}
            {step === "mfa-setup" && (t("firstLogin.set_up_two_factor"))}
            {step === "backup-codes" && (t("firstLogin.backup_codes_2"))}
          </h1>
          <p style={{ fontFamily: "Crimson Text, serif", color: "#858481", marginTop: "0.4rem", fontSize: "0.9rem" }}>
            {step === "password" && (t("firstLogin.choose_a_strong_password_temporary"))}
            {step === "mfa-setup" && (t("firstLogin.scan_the_qr_with_google"))}
            {step === "backup-codes" && (t("firstLogin.save_these_codes_each_can"))}
          </p>
        </div>

        <div className="gold-divider my-7" />

        {step === "password" && (
          <form onSubmit={submitPassword} className="space-y-5">
            <input type="text" name="username" autoComplete="username" defaultValue={presetUsername} hidden readOnly />
            <div>
              <label className="label-lodge" htmlFor="new-password">
                {t("firstLogin.new_password")}
              </label>
              <input
                id="new-password"
                type="password"
                className="input-lodge"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                autoComplete="new-password"
                required
                data-testid="first-login-password-input"
              />
              {/* Live policy indicator */}
              <ul style={{ marginTop: 6, fontSize: "0.72rem", listStyle: "none", padding: 0, color: "#5a4d24" }}>
                {[
                  { ok: newPassword.length >= 8, label: t("firstLogin.8_characters") },
                  { ok: /[A-Z]/.test(newPassword), label: t("firstLogin.1_uppercase_letter") },
                  { ok: /[0-9]/.test(newPassword), label: t("firstLogin.1_digit") },
                  { ok: /[!@#$%^&*()_+\-=[\]{};:,.?/]/.test(newPassword), label: t("firstLogin.1_special_character") },
                ].map((rule, idx) => (
                  <li key={idx} style={{ color: rule.ok ? "#3e6a3c" : "#7c2a2a", fontFamily: "Crimson Text, serif" }}>
                    {rule.ok ? "✓" : "·"} {rule.label}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <label className="label-lodge" htmlFor="confirm-password">
                {t("firstLogin.confirm_new_password")}
              </label>
              <input
                id="confirm-password"
                type="password"
                className="input-lodge"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                autoComplete="new-password"
                required
                data-testid="first-login-password-confirm"
              />
            </div>
            {error && <div className="error-banner" data-testid="first-login-error">{error}</div>}
            <button
              type="submit"
              className="btn-primary-gold w-full"
              disabled={submitting}
              data-testid="first-login-password-submit"
            >
              {submitting ? "..." : (t("firstLogin.continue"))}
            </button>
          </form>
        )}

        {step === "mfa-setup" && (
          <form onSubmit={submitMfa} className="space-y-5">
            <div
              data-testid="first-login-store-badges"
              style={{
                background: "rgba(245, 240, 232, 0.55)",
                border: "1px solid rgba(154, 117, 48, 0.35)",
                borderRadius: 8,
                padding: "10px 12px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 8,
              }}
            >
              <span
                style={{
                  fontFamily: "Cinzel, serif",
                  fontSize: "0.72rem",
                  letterSpacing: "0.18em",
                  color: "#9a7530",
                  textAlign: "center",
                }}
              >
                {t("firstLogin.install_google_authenticator_first")}
              </span>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap", justifyContent: "center" }}>
                <a
                  href="https://apps.apple.com/app/google-authenticator/id388497605"
                  target="_blank"
                  rel="noreferrer"
                  data-testid="first-login-app-store-badge"
                  aria-label="App Store"
                >
                  <img
                    src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg"
                    alt="App Store"
                    height={36}
                    style={{ display: "block" }}
                  />
                </a>
                <a
                  href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2"
                  target="_blank"
                  rel="noreferrer"
                  data-testid="first-login-play-store-badge"
                  aria-label="Google Play"
                >
                  <img
                    src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png"
                    alt="Google Play"
                    height={52}
                    style={{ display: "block" }}
                  />
                </a>
              </div>
            </div>
            <div className="flex justify-center">
              {qrUrl && (
                <img
                  src={qrUrl}
                  alt="QR"
                  width={208}
                  height={208}
                  style={{ background: "#fff", padding: 8, border: "1px solid #c9a84c" }}
                  data-testid="first-login-mfa-qr"
                />
              )}
            </div>
            <div className="kv-label" style={{ fontSize: "0.65rem", textAlign: "center", color: "#9a7530" }}>
              {t("firstLogin.or_paste_this_secret_manually")}
            </div>
            <div
              data-testid="first-login-mfa-secret"
              style={{ textAlign: "center", fontFamily: "JetBrains Mono, monospace", letterSpacing: "0.1em", fontSize: "0.85rem", color: "#34322d", wordBreak: "break-all" }}
            >
              {secret}
            </div>
            <div>
              <label className="label-lodge" htmlFor="mfa-first-code">
                {t("firstLogin.first_6_digit_code")}
              </label>
              <input
                id="mfa-first-code"
                ref={codeRef}
                type="text"
                inputMode="numeric"
                className="input-lodge"
                value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value)}
                autoComplete="one-time-code"
                required
                maxLength={7}
                placeholder="123 456"
                style={{ letterSpacing: "0.4em", textAlign: "center", fontSize: "1.3rem", fontFamily: "JetBrains Mono, monospace" }}
                data-testid="first-login-mfa-code"
              />
            </div>
            {error && <div className="error-banner">{error}</div>}
            <button
              type="submit"
              className="btn-primary-gold w-full"
              disabled={submitting || mfaCode.replace(/\s/g, "").length < 6}
              data-testid="first-login-mfa-submit"
            >
              {submitting ? "..." : (t("firstLogin.activate_mfa"))}
            </button>
            {userRole !== "superadmin" && (
              <button
                type="button"
                className="btn-outline-gold w-full"
                onClick={finish}
                data-testid="first-login-skip-mfa"
              >
                {t("firstLogin.skip_mfa")}
              </button>
            )}
          </form>
        )}

        {step === "backup-codes" && (
          <div className="space-y-5">
            <div
              style={{
                background: "#fdf4e0",
                border: "1px solid #c9a84c",
                padding: "1rem",
                fontFamily: "JetBrains Mono, monospace",
                fontSize: "0.95rem",
                lineHeight: 1.8,
              }}
              data-testid="first-login-backup-codes"
            >
              {backupCodes.map((c) => (
                <div key={c} style={{ letterSpacing: "0.16em", textAlign: "center", color: "#0d1b2a" }}>{c}</div>
              ))}
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                className="btn-outline-gold flex-1 flex items-center justify-center gap-1"
                onClick={copyBackupCodes}
                data-testid="first-login-copy-codes"
              >
                <Copy size={12} /> {t("firstLogin.copy")}
              </button>
              <button
                type="button"
                className="btn-outline-gold flex-1 flex items-center justify-center gap-1"
                onClick={downloadBackupCodes}
                data-testid="first-login-download-codes"
              >
                <CheckCircle2 size={12} /> {t("firstLogin.download")}
              </button>
            </div>
            <button
              type="button"
              className="btn-primary-gold w-full flex items-center justify-center gap-2"
              onClick={finish}
              data-testid="first-login-finish"
            >
              <ShieldCheck size={14} /> {t("firstLogin.enter_the_lodge")} <ArrowRight size={14} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
